// The owner's fresh second factor, asked where the server asks for it (QA-099, QA-685): handing out
// access (making or changing a role, a role's limits, taking back a permission, an invitation that
// gives the owner role) is refused with step_up_required until the owner gives a code. A call run through useStepUp() opens a
// code box then (a FormDialog); a good code steps up and the call runs again. Cancelled, the refusal stands.
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { RpcError } from '@flowology/client';
import { FormDialog, TextField } from '@flowology/ui-web/fl';
import { stepUpWith } from './stepUp.ts';

/** The server asked for a fresh second factor first. */
export const askedStepUp = (e: unknown): boolean =>
  e instanceof RpcError && (e.detail as { reason?: string } | undefined)?.reason === 'step_up_required';

interface Asking {
  again: () => void;
  refuse: () => void;
}

/** `run(call)`: the call, asking for a code and running it again if the server wants one. Render `ask`. */
export function useStepUp(): { run: <T>(call: () => Promise<T>) => Promise<T>; ask: ReactNode } {
  const [asking, setAsking] = useState<Asking | null>(null);
  const run = useCallback(
    <T,>(call: () => Promise<T>): Promise<T> =>
      call().catch((e: unknown) => {
        if (!askedStepUp(e)) throw e;
        return new Promise<T>((resolve, reject) =>
          setAsking({ again: () => void call().then(resolve, reject), refuse: () => reject(e) }),
        );
      }),
    [],
  );
  const ask = asking && (
    <StepUpAsk
      onDone={() => {
        setAsking(null);
        asking.again();
      }}
      onCancel={() => {
        setAsking(null);
        asking.refuse();
      }}
    />
  );
  return { run, ask };
}

function StepUpAsk({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const done = useRef(false);
  const [code, setCode] = useState('');
  return (
    <FormDialog
      isOpen
      title="Confirm it’s you"
      submitLabel="Confirm"
      size="md"
      isDirty={false}
      isSubmitDisabled={!code.trim()}
      onClose={() => (done.current ? onDone() : onCancel())}
      onSubmit={async () => {
        const up = await stepUpWith(code);
        if (!up.ok) throw new Error(up.message);
        done.current = true;
      }}
    >
      <TextField
        label="A code from your authenticator app"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        value={code}
        onChange={setCode}
      />
    </FormDialog>
  );
}
